// REBUY + SHOPIFY STOREFRONT CLIENTS
// ----------------------------------------------------------------
// Provides RebuyClient (Rebuy REST API wrapper) and ShopifyStorefrontClient
// (GraphQL wrapper). Both are exposed as `window.rebuyClient` / `window.shopifyClient`
// so other scripts on the page can use them without import.
//
// Token sources (with safe fallbacks for local dev):
//   apiAccessToken    ← window.Rebuy.shop.api_key
//   shopifyAccessToken ← window.Rebuy.shop.storefront_access_token
//                       (set in Rebuy Admin → Settings → Integrations → Shopify)
//                       OR replace STOREFRONT_TOKEN_FALLBACK with a custom-app token
//                       (Shopify Admin → Apps → Develop apps → Storefront API access)
//   shopifyStore      ← window.Shopify.shop  (e.g. "os-jkfd.myshopify.com")

class RebuyClient {
  constructor({ clientToken }) {
    this.maxCacheMinutes = 30;
    this.storageKey = "productJSON";
    this.clientToken = clientToken;
  }

  async query(url, params) {
    return fetch(
      `https://rebuyengine.com/${url}?${new URLSearchParams({
        key: this.clientToken,
        ...params,
      })}`,
      {
        method: "GET",
        headers: { "Content-type": "application/json; charset=UTF-8" },
      },
    ).then((res) => res.json());
  }

  // Fetches a single product in Rebuy's product format (matches what the widget passes to the quick view). Used by the quick-view family swatch click to swap context to a sibling product without leaving the modal.
  async getProductByHandle(handle) {
    if (!handle) return null;
    const cacheKey = `${this.storageKey}:handle:${handle}`;
    const cacheMax = Date.now() - this.maxCacheMinutes * 60 * 1000;
    try {
      const cached = JSON.parse(localStorage.getItem(cacheKey));
      if (cached && cached.cachedAt > cacheMax) return cached;
    } catch (_) {}
    const response = await this.query('api/v1/products/static', {
      metafields: 'yes',
      handles: handle,
      limit: 1,
    });
    const product = response && response.data && response.data[0];
    if (product) {
      product.cachedAt = Date.now();
      this._safeCacheWrite(cacheKey, JSON.stringify(product));
    }
    return product;
  }

  async getProductsByIDs(productIDs) {
    const products = [];
    const staticIDs = [];
    productIDs.forEach((id) => {
      const cached = this.getProductFromCacheByID(id);
      if (cached) products.push(cached);
      else staticIDs.push(id);
    });
    if (staticIDs.length > 0) {
      const response = await this.query("api/v1/products/static", {
        metafields: "yes",
        shopify_product_ids: staticIDs.join(","),
        limit: 100,
      });
      response.data.forEach((product) =>
        this.saveProductToCacheByID(product.id, product),
      );
      products.push(...response.data);
    }
    return products.sort(
      (a, b) => productIDs.indexOf(a.id) - productIDs.indexOf(b.id),
    );
  }

  getProductFromCacheByID(productID) {
    const cacheMax = Date.now() - this.maxCacheMinutes * 60 * 1000;
    const productJSON = JSON.parse(
      localStorage.getItem(`${this.storageKey}:${productID}`),
    );
    return productJSON && productJSON.cachedAt > cacheMax ? productJSON : null;
  }

  saveProductToCacheByID(productID, productJSON) {
    productJSON.cachedAt = Date.now();
    this._safeCacheWrite(
      `${this.storageKey}:${productID}`,
      JSON.stringify(productJSON),
    );
  }

  _safeCacheWrite(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      if (e.name === "QuotaExceededError") {
        this._evictOldestEntries();
        try {
          localStorage.setItem(key, value);
        } catch (_) {}
      }
    }
  }

  _evictOldestEntries() {
    const rebuyPrefixes = [
      "productJSON:",
      "metaobject_",
      "product_family_",
      "product_grouping_",
      "product_",
    ];
    const entries = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (rebuyPrefixes.some((p) => key.startsWith(p))) {
        try {
          const parsed = JSON.parse(localStorage.getItem(key));
          entries.push({ key, cachedAt: parsed?.cachedAt || 0 });
        } catch (_) {
          entries.push({ key, cachedAt: 0 });
        }
      }
    }
    entries.sort((a, b) => a.cachedAt - b.cachedAt);
    const removeCount = Math.max(1, Math.ceil(entries.length / 2));
    for (let i = 0; i < removeCount; i++)
      localStorage.removeItem(entries[i].key);
  }
}

class ShopifyStorefrontClient extends RebuyClient {
  constructor({ store, apiAccessToken, apiVersion = "2025-07", clientToken }) {
    super({ clientToken });
    this.store = store;
    this.apiVersion = apiVersion;
    this.apiAccessToken = apiAccessToken;
  }

  async query(query, variables = {}) {
    try {
      const response = await fetch(
        `https://${this.store}/api/${this.apiVersion}/graphql.json`,
        {
          method: "POST",
          body: JSON.stringify({ query, variables }),
          headers: {
            "Content-type": "application/json; charset=UTF-8",
            "X-Shopify-Storefront-Access-Token": this.apiAccessToken,
          },
        },
      );
      const result = await response.json();
      if (result.errors) {
        console.error("Storefront GraphQL errors:", result.errors);
        return null;
      }
      return result?.data;
    } catch (error) {
      console.error("Storefront API request error:", error);
      return null;
    }
  }

  async fetchWithCache(cacheKey, query, variables = {}) {
    const cacheMax = Date.now() - this.maxCacheMinutes * 60 * 1000;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (parsed && parsed.cachedAt > cacheMax) return parsed.data;
        localStorage.removeItem(cacheKey);
      } catch (_) {
        localStorage.removeItem(cacheKey);
      }
    }
    const data = await this.query(query, variables);
    try {
      localStorage.setItem(
        cacheKey,
        JSON.stringify({ data, cachedAt: Date.now() }),
      );
    } catch (e) {
      if (e.name === "QuotaExceededError") {
        this._evictOldestEntries();
        try {
          localStorage.setItem(
            cacheKey,
            JSON.stringify({ data, cachedAt: Date.now() }),
          );
        } catch (_) {}
      }
    }
    return data;
  }

  // Fetches a product's `custom.product_family` metafield references — all sibling products in the family with media + variants for swatch resolution.
  async getProductFamily(handle) {
    if (!handle) return null;
    const query = `
      query ProductFamily($handle: String!) {
        product(handle: $handle) {
          id
          handle
          metafield(namespace: "custom", key: "product_family") {
            references(first: 50) {
              nodes {
                ... on Product {
                  id
                  handle
                  title
                  availableForSale
                  featuredImage { url altText }
                  options { name values }
                  media(first: 20) {
                    nodes {
                      ... on MediaImage {
                        image { url altText }
                      }
                    }
                  }
                  variants(first: 100) {
                    nodes {
                      id
                      title
                      availableForSale
                      selectedOptions { name value }
                      image { url }
                      price { amount currencyCode }
                      compareAtPrice { amount currencyCode }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;
    const cacheKey = `product_family_${handle}`;
    return this.fetchWithCache(cacheKey, query, { handle });
  }
}

const apiAccessToken =
  window.Rebuy?.shop?.api_key || "de3181853801475b4efd19d6c4c777a89ccd0072";
const shopifyAccessToken =
  window.Rebuy?.shop?.storefront_access_token ||
  "245ba9a105714a9e75ae5e9c8d7a2f46";
const shopifyStore = window.Shopify?.shop || "os-jkfd.myshopify.com";

window.rebuyClient = new RebuyClient({ clientToken: apiAccessToken });
window.shopifyClient = new ShopifyStorefrontClient({
  store: shopifyStore,
  apiAccessToken: shopifyAccessToken,
  apiVersion: "2026-04",
});
